import type { PropsWithChildren} from 'react';
import { useMemo } from 'react';

import classNames from 'classnames';

import styles from './styles.module.scss';

interface ProgressBarProps {
  minValue?: number;
  maxValue?: number;
  value?: number;
  appearance?: {
    container?: string;
    indicator?: {
      paper?: string;
      ink?: string;
      mask?: string;
    };
  };
  overrides?: Record<`--${string}`, string | number>;
}

const ProgressBar = ({
  minValue = 0,
  maxValue = 1,
  value = 0,
  appearance,
  overrides,
  children,
}: PropsWithChildren<ProgressBarProps>) => {
  const progressPercent = useMemo(() => {
    return ((Math.min(value, maxValue) - minValue) / (maxValue - minValue)) * 100;
  }, [maxValue, minValue, value]);

  const maskStyle = { transform: `translateX(${progressPercent - 100}%)` };

  const inkStyle = { transform: `translateX(${100 - progressPercent}%)` };

  return (
    <div className={classNames(styles.progressBar, appearance?.container)} style={overrides}>
      <div className={classNames(styles.indicatorPaper, appearance?.indicator?.paper)}>
        <div
          className={classNames(styles.indicatorMask, appearance?.indicator?.mask)}
          style={maskStyle}
        >
          <div
            className={classNames(styles.indicatorInk, appearance?.indicator?.ink)}
            style={inkStyle}
          ></div>
        </div>
      </div>

      {children}
    </div>
  );
};

type ProgressBarSlotName = 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';

interface ProgressBarSlotProps {
  name: ProgressBarSlotName;
  className?: string;
}

const ProgressBarSlot = ({
  name,
  className,
  children,
}: PropsWithChildren<ProgressBarSlotProps>) => {
  return (
    <div className={classNames(styles.slot, className)} data-progress-bar-slot-name={name}>
      {children}
    </div>
  );
};

ProgressBarSlot.displayName = 'ProgressBar.Slot';

ProgressBar.Slot = ProgressBarSlot;

export { ProgressBar, type ProgressBarProps };
